/**
 * Comprehensive student data clearing utility for mobile classroom privacy
 * Ensures no student data persists between sessions
 */

export const clearAllStudentData = async (): Promise<void> => {
  try {
    // Clear localStorage
    const localStorageKeysToRemove = [
      'token',
      'userData',
      'auth_error',
      'lastStartedTest',
      'lastTestStartTime',
      'selectedTest',
      'persist:root'
    ];

    localStorageKeysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });

    // Clear any app-specific prefixed keys
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('liveolympiad_') ||
          key.startsWith('student_') ||
          key.startsWith('test_') ||
          key.startsWith('session_') ||
          key.startsWith('olympiad_')) {
        localStorage.removeItem(key);
      }
    });

    // Clear sessionStorage completely
    sessionStorage.clear();

    // Clear Service Worker caches (for PWA/WebView)
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
    }

    // Clear IndexedDB if used (future-proofing)
    if ('indexedDB' in window) {
      try {
        // Clear any potential IndexedDB databases
        const databases = ['liveolympiad', 'student-data', 'test-progress'];
        databases.forEach(dbName => {
          const deleteRequest = indexedDB.deleteDatabase(dbName);
        });
      } catch (error) {
      }
    }

    // Mobile WebView specific clearing
    if (window.location.hostname === '10.0.0.10') {
      try {
        // Clear any WebView-specific caches
        if ((window as any).Android) {
          // Android WebView interface (if implemented)
          try {
            (window as any).Android.clearCache();
          } catch (e) {
          }
        }

        // Clear audio cache (specific to LiveOlympiad)
        if ('caches' in window) {
          const audioCache = await caches.open('audio-cache');
          const audioRequests = await audioCache.keys();
          await Promise.all(
            audioRequests.map(request => audioCache.delete(request))
          );
        }
      } catch (error) {
      }
    }

  } catch (error) {
    throw error;
  }
};

/**
 * Check if running in mobile classroom environment
 */
export const isMobileClassroom = (): boolean => {
  return window.location.hostname === '10.0.0.10';
};

/**
 * Get data clearing summary for debugging
 */
export const getDataClearingSummary = (): string => {
  const localStorageSize = Object.keys(localStorage).length;
  const sessionStorageSize = Object.keys(sessionStorage).length;
  const isMobile = isMobileClassroom();

  return `Data clearing summary:
    - Environment: ${isMobile ? 'Mobile Classroom' : 'Web Browser'}
    - localStorage keys remaining: ${localStorageSize}
    - sessionStorage keys remaining: ${sessionStorageSize}
    - Cache API available: ${'caches' in window}
    - IndexedDB available: ${'indexedDB' in window}`;
};